import { useEffect, useRef, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useTourStore } from './useTourStore'
import { TOUR_STEPS, routeMatches } from './tourSteps'
import TourSpotlight from './TourSpotlight'
import TourTooltip from './TourTooltip'
import { updateMyPreferences } from '../../api'
import { analytics } from '../../lib/analytics'

const POLL_LIMIT_MS = 5000 // stop polling after 5 s if element never appears

/**
 * Mounts as a singleton inside <AppShell> (needs Router context for useLocation).
 * Handles:
 *   • Target element lookup via rAF polling
 *   • Auto-advance when the user navigates to the next step's route
 *   • Spotlight + tooltip rendering (portal'd to document.body)
 *   • Keyboard shortcuts: Esc = skip, → = next, ← = back
 *   • Scroll / resize updates so the spotlight tracks the target
 */
export default function TourProvider() {
  const location = useLocation()
  const { active, step, completed, next, back, skip, complete } = useTourStore()
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)

  const currentStep = TOUR_STEPS[step]
  const total = TOUR_STEPS.length

  // ─── Sync completion to backend (fire-and-forget) ─────────────────────────
  const syncedRef = useRef(false)
  useEffect(() => {
    if (completed && !syncedRef.current) {
      syncedRef.current = true
      updateMyPreferences({ tour_completed: true }).catch(() => { /* non-critical */ })
      analytics.track('tour_completed')
    }
    if (!completed) {
      syncedRef.current = false
    }
  }, [completed])

  // ─── Auto-advance on route change ──────────────────────────────────────────
  // When the user navigates naturally (e.g. clicks "New decision" and lands on
  // the builder), check whether the new pathname matches the NEXT step's route.
  useEffect(() => {
    if (!active || !currentStep) return
    if (routeMatches(location.pathname, currentStep.route)) return
    const nextStep = TOUR_STEPS[step + 1]
    if (nextStep && routeMatches(location.pathname, nextStep.route)) {
      next()
    }
  }, [location.pathname, active, step]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Target element finder ──────────────────────────────────────────────────
  useEffect(() => {
    setTargetRect(null)
    if (!active || !currentStep) return
    if (!routeMatches(location.pathname, currentStep.route)) return

    let cancelled = false
    let rafId: number
    let observer: ResizeObserver | null = null
    const startTime = performance.now()

    const findTarget = () => {
      if (cancelled) return
      const el = document.querySelector(currentStep.target) as HTMLElement | null
      if (el) {
        // Bring target into view so the spotlight is visible
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const readRect = () => {
          if (!cancelled) setTargetRect(el.getBoundingClientRect())
        }
        readRect()
        // Keep spotlight in sync when the target resizes (e.g. form expansion)
        observer = new ResizeObserver(readRect)
        observer.observe(el)
        return
      }
      // Polling: keep trying until element appears or timeout
      if (currentStep.waitForElement && performance.now() - startTime < POLL_LIMIT_MS) {
        rafId = requestAnimationFrame(findTarget)
      }
      // If we hit the timeout: leave targetRect null → tooltip renders as fallback (bottom-center)
    }

    rafId = requestAnimationFrame(findTarget)

    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      observer?.disconnect()
    }
  }, [active, step, location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Track target position on scroll / window resize ──────────────────────
  const updateRect = useCallback(() => {
    if (!currentStep) return
    const el = document.querySelector(currentStep.target) as HTMLElement | null
    if (el) setTargetRect(el.getBoundingClientRect())
  }, [currentStep])

  useEffect(() => {
    if (!active || !targetRect) return
    window.addEventListener('scroll', updateRect, { passive: true, capture: true })
    window.addEventListener('resize', updateRect)
    return () => {
      window.removeEventListener('scroll', updateRect, { capture: true })
      window.removeEventListener('resize', updateRect)
    }
  }, [active, targetRect, updateRect])

  // ─── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { skip(); return }
      if (e.key === 'ArrowRight') {
        if (step === total - 1) complete()
        else next()
        return
      }
      if (e.key === 'ArrowLeft') { back(); return }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, step, total, next, back, skip, complete])

  if (!active || !currentStep) return null

  return (
    <>
      {targetRect && (
        <TourSpotlight
          key={`spotlight-${step}`}
          rect={targetRect}
          padding={currentStep.spotlightPadding}
        />
      )}
      <TourTooltip
        key={`tooltip-${step}`}
        step={currentStep}
        stepIndex={step}
        total={total}
        targetRect={targetRect}
      />
    </>
  )
}
